import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw, X } from "lucide-react";

/**
 * NewVersionBanner
 * Shows a sticky bottom banner whenever a new service worker version is waiting.
 * Clicking "Reload" sends SKIP_WAITING to the waiting SW and reloads the page
 * once the `controllerchange` fires (or 3s fallback).
 */
export default function NewVersionBanner() {
  const { t } = useTranslation();
  const [waitingSW, setWaitingSW] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;

    let cancelled = false;

    const trackReg = (reg) => {
      if (!reg || cancelled) return;

      // If a worker is already waiting when we hook in, surface it immediately.
      if (reg.waiting && navigator.serviceWorker.controller) {
        setWaitingSW(reg.waiting);
      }

      const onUpdateFound = () => {
        const installing = reg.installing;
        if (!installing) return;
        installing.addEventListener("statechange", () => {
          if (installing.state === "installed" && navigator.serviceWorker.controller) {
            setWaitingSW(installing);
            setDismissed(false);
          }
        });
      };
      reg.addEventListener("updatefound", onUpdateFound);
    };

    navigator.serviceWorker.getRegistration().then(trackReg);
    // Some browsers register the SW after this component mounts; listen for ready too.
    navigator.serviceWorker.ready.then(trackReg).catch(() => {});

    return () => { cancelled = true; };
  }, []);

  if (!waitingSW || dismissed) return null;

  const reload = () => {
    // La recarga real puede tardar hasta los 3s del fallback (esperando el
    // controllerchange) sin ningún indicio visual de que el clic hizo algo
    // -- se marca "reloading" de inmediato para que el icono gire y el botón
    // quede deshabilitado mientras tanto.
    setReloading(true);
    let reloaded = false;
    const doReload = () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", doReload, { once: true });
    try { waitingSW.postMessage({ type: "SKIP_WAITING" }); } catch (_e) {}
    setTimeout(doReload, 3000);
  };

  return (
    <div
      className="fixed z-[60] left-3 right-3 md:left-auto md:right-6 md:max-w-sm bottom-[calc(env(safe-area-inset-bottom,0px)+80px)] md:bottom-6"
      data-testid="new-version-banner"
    >
      <div className="bg-[#1C1C1E] dark:bg-[#2C2C2E] text-white rounded-[14px] shadow-xl p-3 flex items-center gap-3">
        <div className="flex items-center justify-center flex-shrink-0">
          <RefreshCw className={`w-4 h-4 ${reloading ? "animate-spin" : ""}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate">{t("new_version.title")}</p>
        </div>
        <button
          onClick={reload}
          disabled={reloading}
          className="h-9 px-3.5 rounded-[9px] bg-[#ffffff] text-[#1C1C1E] text-sm font-semibold hover:bg-[#e5e5ea] disabled:opacity-70 disabled:cursor-wait"
          data-testid="new-version-reload"
        >
          {reloading ? t("new_version.reloading") : t("new_version.reload")}
        </button>
        <button
          onClick={() => setDismissed(true)}
          disabled={reloading}
          aria-label={t("new_version.dismiss")}
          className="w-9 h-9 rounded-full hover:bg-white/15 flex items-center justify-center flex-shrink-0 disabled:opacity-50"
          data-testid="new-version-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
